/**
 * Bibliographic citations (CSL-JSON-compatible fields) + in-text uses.
 * The Works Cited / References spine item is generated from uses, not the full library.
 */

import type { CitationStyle } from "./bookType";
import { bibliographyTitleForStyle } from "./bookType";

export const CITATION_ITEM_TYPES = [
  "book",
  "article-journal",
  "webpage",
  "chapter",
  "paper-conference",
  "interview",
  "report",
  "post-weblog",
] as const;

export type CitationItemType = (typeof CITATION_ITEM_TYPES)[number];

export type CslName = {
  family?: string;
  given?: string;
  literal?: string;
};

export type CslDate = {
  "date-parts"?: number[][];
  raw?: string;
};

/** Subset of CSL-JSON used for APA / MLA / Chicago author-date. */
export type CslJsonItem = {
  id?: string;
  type?: CitationItemType | string;
  title?: string;
  author?: CslName[];
  issued?: CslDate;
  publisher?: string;
  "publisher-place"?: string;
  "container-title"?: string;
  volume?: string | number;
  issue?: string | number;
  page?: string;
  DOI?: string;
  URL?: string;
  ISBN?: string;
};

export type Citation = {
  id: string;
  csl: CslJsonItem;
  createdAt: string;
  updatedAt: string;
};

export type CitationUse = {
  id: string;
  citationId: string;
  chapterId: string;
  markId: string;
  locator?: string;
};

export type CitationState = {
  style: CitationStyle;
  items: Citation[];
  uses: CitationUse[];
};

function nowIso(): string {
  return new Date().toISOString();
}

function newId(prefix: string): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return `${prefix}-${c.randomUUID()}`;
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function isCitationItemType(value: unknown): value is CitationItemType {
  return typeof value === "string" && (CITATION_ITEM_TYPES as readonly string[]).includes(value);
}

function parseNames(value: unknown): CslName[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const rec = item as Record<string, unknown>;
      const name: CslName = {};
      if (typeof rec.family === "string") name.family = rec.family;
      if (typeof rec.given === "string") name.given = rec.given;
      if (typeof rec.literal === "string") name.literal = rec.literal;
      return name.family || name.given || name.literal ? name : null;
    })
    .filter((n): n is CslName => n != null);
}

function parseCsl(raw: unknown): CslJsonItem {
  if (!raw || typeof raw !== "object") return { type: "book", title: "Untitled" };
  const rec = raw as Record<string, unknown>;
  const issuedRaw = rec.issued;
  let issued: CslDate | undefined;
  if (issuedRaw && typeof issuedRaw === "object") {
    const ir = issuedRaw as Record<string, unknown>;
    issued = {
      "date-parts": Array.isArray(ir["date-parts"]) ? (ir["date-parts"] as number[][]) : undefined,
      raw: typeof ir.raw === "string" ? ir.raw : undefined,
    };
  }
  return {
    id: typeof rec.id === "string" ? rec.id : undefined,
    type: typeof rec.type === "string" ? rec.type : "book",
    title: typeof rec.title === "string" ? rec.title : undefined,
    author: parseNames(rec.author),
    issued,
    publisher: typeof rec.publisher === "string" ? rec.publisher : undefined,
    "publisher-place": typeof rec["publisher-place"] === "string" ? rec["publisher-place"] : undefined,
    "container-title": typeof rec["container-title"] === "string" ? rec["container-title"] : undefined,
    volume: typeof rec.volume === "string" || typeof rec.volume === "number" ? rec.volume : undefined,
    issue: typeof rec.issue === "string" || typeof rec.issue === "number" ? rec.issue : undefined,
    page: typeof rec.page === "string" ? rec.page : undefined,
    DOI: typeof rec.DOI === "string" ? rec.DOI : undefined,
    URL: typeof rec.URL === "string" ? rec.URL : undefined,
    ISBN: typeof rec.ISBN === "string" ? rec.ISBN : undefined,
  };
}

export function emptyCitationState(style: CitationStyle = "apa"): CitationState {
  return { style, items: [], uses: [] };
}

export function getCitationState(metadata: Record<string, unknown> | null | undefined): CitationState {
  const styleRaw = metadata?.citationStyle;
  const style =
    styleRaw === "mla" || styleRaw === "chicago" || styleRaw === "apa" ? styleRaw : "apa";
  const items = Array.isArray(metadata?.citations)
    ? metadata.citations
        .map((raw) => {
          if (!raw || typeof raw !== "object") return null;
          const rec = raw as Record<string, unknown>;
          const id = typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : "";
          if (!id) return null;
          return {
            id,
            csl: parseCsl(rec.csl ?? rec.cslJson ?? rec),
            createdAt: typeof rec.createdAt === "string" ? rec.createdAt : nowIso(),
            updatedAt: typeof rec.updatedAt === "string" ? rec.updatedAt : nowIso(),
          } satisfies Citation;
        })
        .filter((c): c is Citation => c != null)
    : [];
  const uses = Array.isArray(metadata?.citationUses)
    ? metadata.citationUses
        .map((raw) => {
          if (!raw || typeof raw !== "object") return null;
          const rec = raw as Record<string, unknown>;
          const id = typeof rec.id === "string" ? rec.id : "";
          const citationId = typeof rec.citationId === "string" ? rec.citationId : "";
          const chapterId = typeof rec.chapterId === "string" ? rec.chapterId : "";
          const markId = typeof rec.markId === "string" ? rec.markId : "";
          if (!id || !citationId || !chapterId || !markId) return null;
          const use: CitationUse = { id, citationId, chapterId, markId };
          if (typeof rec.locator === "string" && rec.locator.trim()) use.locator = rec.locator;
          return use;
        })
        .filter((u): u is CitationUse => u != null)
    : [];
  return { style, items, uses };
}

export function withCitationsInMetadata(
  metadata: Record<string, unknown>,
  state: CitationState
): Record<string, unknown> {
  return {
    ...metadata,
    citationStyle: state.style,
    citations: state.items,
    citationUses: state.uses,
  };
}

export function createCitation(csl: CslJsonItem, id?: string): Citation {
  const at = nowIso();
  const citationId = id?.trim() || newId("cite");
  return {
    id: citationId,
    csl: { ...csl, id: csl.id || citationId },
    createdAt: at,
    updatedAt: at,
  };
}

export function createCitationUse(input: Omit<CitationUse, "id"> & { id?: string }): CitationUse {
  return {
    id: input.id?.trim() || newId("citeuse"),
    citationId: input.citationId,
    chapterId: input.chapterId,
    markId: input.markId,
    locator: input.locator?.trim() || undefined,
  };
}

export function upsertCitation(state: CitationState, citation: Citation): CitationState {
  const next = { ...citation, updatedAt: nowIso() };
  const index = state.items.findIndex((c) => c.id === citation.id);
  if (index < 0) return { ...state, items: [...state.items, next] };
  const items = [...state.items];
  items[index] = { ...items[index], ...next, createdAt: items[index].createdAt };
  return { ...state, items };
}

export function removeCitation(state: CitationState, citationId: string): CitationState {
  return {
    ...state,
    items: state.items.filter((c) => c.id !== citationId),
    uses: state.uses.filter((u) => u.citationId !== citationId),
  };
}

export function upsertCitationUse(state: CitationState, use: CitationUse): CitationState {
  const index = state.uses.findIndex((u) => u.id === use.id || u.markId === use.markId);
  if (index < 0) return { ...state, uses: [...state.uses, use] };
  const uses = [...state.uses];
  uses[index] = use;
  return { ...state, uses };
}

export function removeCitationUse(state: CitationState, markId: string): CitationState {
  return { ...state, uses: state.uses.filter((u) => u.markId !== markId && u.id !== markId) };
}

export function replaceChapterCitationUses(
  state: CitationState,
  chapterId: string,
  chapterUses: CitationUse[]
): CitationState {
  return {
    ...state,
    uses: [...state.uses.filter((u) => u.chapterId !== chapterId), ...chapterUses],
  };
}

export function citedItemsInOrder(state: CitationState): Citation[] {
  const seen = new Set<string>();
  const ordered: Citation[] = [];
  for (const use of state.uses) {
    if (seen.has(use.citationId)) continue;
    const item = state.items.find((c) => c.id === use.citationId);
    if (!item) continue;
    seen.add(use.citationId);
    ordered.push(item);
  }
  return ordered.sort((a, b) => bibliographySortKey(a).localeCompare(bibliographySortKey(b)));
}

function yearFromCsl(csl: CslJsonItem): string {
  const parts = csl.issued?.["date-parts"]?.[0];
  if (parts && Number.isFinite(parts[0])) return String(parts[0]);
  const raw = csl.issued?.raw?.trim();
  if (raw) {
    const match = raw.match(/\d{4}/);
    if (match) return match[0];
  }
  return "n.d.";
}

function familyName(name: CslName | undefined): string {
  if (!name) return "";
  if (name.family?.trim()) return name.family.trim();
  if (name.literal?.trim()) return name.literal.trim();
  return (name.given ?? "").trim();
}

function formatNameList(authors: CslName[] | undefined, style: CitationStyle): string {
  const list = authors?.filter((a) => familyName(a)) ?? [];
  if (!list.length) return "Anonymous";
  const fmt = (name: CslName): string => {
    const family = familyName(name);
    const given = (name.given ?? "").trim();
    if (style === "apa") {
      if (!given) return family;
      const initials = given
        .split(/\s+/)
        .map((p) => `${p.charAt(0).toUpperCase()}.`)
        .join(" ");
      return `${family}, ${initials}`;
    }
    if (!given) return family;
    return `${family}, ${given}`;
  };
  if (list.length === 1) return fmt(list[0]);
  if (list.length === 2) {
    return style === "apa" ? `${fmt(list[0])}, & ${fmt(list[1])}` : `${fmt(list[0])}, and ${fmt(list[1])}`;
  }
  if (style === "apa" && list.length > 2) {
    return `${fmt(list[0])}, et al.`;
  }
  const head = list.slice(0, -1).map(fmt).join(", ");
  return `${head}, and ${fmt(list[list.length - 1])}`;
}

function leadAuthorFamily(csl: CslJsonItem): string {
  return familyName(csl.author?.[0]) || "Anonymous";
}

function bibliographySortKey(citation: Citation): string {
  return `${leadAuthorFamily(citation.csl).toLowerCase()} ${yearFromCsl(citation.csl)} ${citation.csl.title ?? ""}`;
}

export function formatInTextCitation(
  citation: Citation,
  style: CitationStyle,
  locator?: string
): string {
  const author = leadAuthorFamily(citation.csl);
  const year = yearFromCsl(citation.csl);
  const loc = locator?.trim();
  if (style === "mla") {
    return loc ? `(${author} ${loc})` : `(${author})`;
  }
  if (style === "chicago") {
    return loc ? `(${author} ${year}, ${loc})` : `(${author} ${year})`;
  }
  return loc ? `(${author}, ${year}, ${loc})` : `(${author}, ${year})`;
}

export function formatBibliographyEntry(citation: Citation, style: CitationStyle): string {
  const csl = citation.csl;
  const authors = formatNameList(csl.author, style);
  const year = yearFromCsl(csl);
  const title = (csl.title ?? "Untitled").trim();
  const container = (csl["container-title"] ?? "").trim();
  const publisher = (csl.publisher ?? "").trim();
  const url = (csl.URL ?? "").trim();
  const doi = (csl.DOI ?? "").trim();
  const pages = (csl.page ?? "").trim();

  if (style === "mla") {
    const work = container ? `*${title}.*` : `*${title}.*`;
    const bits = [authors.endsWith(".") ? authors : `${authors}.`, work];
    if (container) bits.push(container + (pages ? `, pp. ${pages}` : "") + ".");
    if (publisher) bits.push(`${publisher}, ${year === "n.d." ? "n.d." : year}.`);
    else bits.push(`${year === "n.d." ? "n.d." : year}.`);
    if (url) bits.push(url);
    return bits.join(" ").replace(/\s+/g, " ").trim();
  }

  if (style === "chicago") {
    const bits = [`${authors}.`, `"${title}."`];
    if (container) bits.push(container + (pages ? ` ${pages}` : "") + ".");
    if (publisher) bits.push(`${publisher}, ${year}.`);
    else bits.push(`${year}.`);
    if (url) bits.push(url);
    return bits.join(" ").replace(/\s+/g, " ").trim();
  }

  const italicTitle = container ? title : title;
  const bits = [`${authors} (${year}).`, `${italicTitle}.`];
  if (container) {
    const vol = csl.volume != null ? ` ${csl.volume}` : "";
    const issue = csl.issue != null ? `(${csl.issue})` : "";
    bits.push(`${container}${vol}${issue}${pages ? `, ${pages}` : ""}.`);
  }
  if (publisher) bits.push(`${publisher}.`);
  if (doi) bits.push(`https://doi.org/${doi.replace(/^https?:\/\/doi.org\//i, "")}`);
  else if (url) bits.push(url);
  return bits.join(" ").replace(/\s+/g, " ").trim();
}

export function bibliographyPageTitle(style: CitationStyle): string {
  return bibliographyTitleForStyle(style);
}

/** TipTap JSON for the generated bibliography spine item. */
export function bibliographyTipTapContent(state: CitationState): string {
  const items = citedItemsInOrder(state);
  const paragraphs =
    items.length === 0
      ? [
          {
            type: "paragraph",
            attrs: { id: "" },
            content: [{ type: "text", text: "No sources cited yet." }],
          },
        ]
      : items.map((citation) => ({
          type: "paragraph",
          attrs: { id: "" },
          content: [{ type: "text", text: formatBibliographyEntry(citation, state.style) }],
        }));
  return JSON.stringify({ type: "doc", content: paragraphs });
}

export function collectCitationMarkIdsFromContent(content: string): string[] {
  return collectCitationUsesFromContent("", content).map((u) => u.markId);
}

export function collectCitationUsesFromContent(chapterId: string, content: string): CitationUse[] {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content) as unknown;
    const uses: CitationUse[] = [];
    walkMarks(parsed, (mark) => {
      if (mark.type !== "citation") return;
      const citationId = typeof mark.attrs?.citationId === "string" ? mark.attrs.citationId : "";
      const markId = typeof mark.attrs?.markId === "string" ? mark.attrs.markId : "";
      if (!citationId || !markId) return;
      uses.push({
        id: `citeuse-${markId}`,
        citationId,
        chapterId,
        markId,
        locator: typeof mark.attrs?.locator === "string" ? mark.attrs.locator : undefined,
      });
    });
    return uses;
  } catch {
    return [];
  }
}

function walkMarks(
  node: unknown,
  visit: (mark: { type?: string; attrs?: Record<string, unknown> }) => void
): void {
  if (!node || typeof node !== "object") return;
  const rec = node as Record<string, unknown>;
  if (Array.isArray(rec.marks)) {
    for (const mark of rec.marks) {
      if (mark && typeof mark === "object") visit(mark as { type?: string; attrs?: Record<string, unknown> });
    }
  }
  if (Array.isArray(rec.content)) {
    for (const child of rec.content) walkMarks(child, visit);
  }
}

export function formatCitationLibraryForChat(items: Citation[], limit = 20): string {
  if (!items.length) return "";
  const lines = items.slice(0, limit).map((c) => {
    const year = yearFromCsl(c.csl);
    return `- ${c.id} · ${leadAuthorFamily(c.csl)} (${year}). ${c.csl.title ?? "Untitled"}`;
  });
  return ["Citation library:", ...lines].join("\n");
}

export type BibliographyChapterPatch = {
  id: string;
  title: string;
  kind: "bibliography";
  content: string;
};

/** Update or describe a generated bibliography spine item. */
export function bibliographyChapterFields(state: CitationState): {
  title: string;
  kind: "bibliography";
  content: string;
} {
  return {
    title: bibliographyPageTitle(state.style),
    kind: "bibliography",
    content: bibliographyTipTapContent(state),
  };
}
